let audioCtx: AudioContext | null = null;

function getCtx(): AudioContext {
  if (!audioCtx) audioCtx = new AudioContext();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

function isMuted(): boolean {
  return localStorage.getItem("ho-sound-muted") === "true";
}

export function setMuted(muted: boolean) {
  localStorage.setItem("ho-sound-muted", String(muted));
}

export function getMuted(): boolean {
  return isMuted();
}

function tone(freq: number, duration: number, type: OscillatorType = "sine", gain = 0.15) {
  if (isMuted()) return;
  const ctx = getCtx();
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(gain, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  osc.connect(g).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + duration);
}

// Task complete — short rising two-tone
export function playComplete() {
  if (isMuted()) return;
  tone(440, 0.1, "triangle", 0.12);
  setTimeout(() => tone(660, 0.15, "triangle", 0.12), 80);
}

// Focus mode start — low hum ramp-up
export function playFocusStart() {
  if (isMuted()) return;
  const ctx = getCtx();
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(80, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + 0.5);
  g.gain.setValueAtTime(0.08, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
  osc.connect(g).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.6);
}

// Focus cycle complete — higher chime
export function playCycleComplete() {
  if (isMuted()) return;
  tone(880, 0.15, "sine", 0.1);
  setTimeout(() => tone(1100, 0.2, "sine", 0.1), 100);
}

// Level up — ascending arpeggio
export function playLevelUp() {
  if (isMuted()) return;
  tone(440, 0.15, "triangle", 0.12);
  setTimeout(() => tone(554, 0.15, "triangle", 0.12), 150);
  setTimeout(() => tone(660, 0.25, "triangle", 0.15), 300);
}

// Daily target cleared — harmonized fanfare
export function playDailyTarget() {
  if (isMuted()) return;
  tone(440, 0.2, "triangle", 0.1);
  tone(554, 0.2, "triangle", 0.1);
  setTimeout(() => {
    tone(660, 0.3, "triangle", 0.12);
    tone(880, 0.3, "triangle", 0.08);
  }, 180);
}

// Subtask check — quick tick
export function playTick() {
  if (isMuted()) return;
  tone(1200, 0.04, "square", 0.06);
}

// XP gain — soft blip, pitch scales with amount
export function playXpGain(amount: number = 30) {
  if (isMuted()) return;
  const pitch = 400 + Math.min(amount, 250) * 2;
  tone(pitch, 0.08, "sine", 0.08);
}

// Streak milestone — deep pulse + sweep
export function playStreakMilestone() {
  if (isMuted()) return;
  tone(110, 0.2, "sine", 0.12);
  setTimeout(() => {
    const ctx = getCtx();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(200, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.3);
    g.gain.setValueAtTime(0.06, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.connect(g).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
  }, 150);
}

// Timer warning — pulse on last 60s
export function playTimerPulse() {
  if (isMuted()) return;
  tone(200, 0.1, "sine", 0.05);
}

// Abort/fail — descending tone
export function playAbort() {
  if (isMuted()) return;
  tone(440, 0.1, "sawtooth", 0.08);
  setTimeout(() => tone(330, 0.15, "sawtooth", 0.08), 80);
  setTimeout(() => tone(220, 0.2, "sawtooth", 0.06), 160);
}
